/**
 * CustomPaginacao.tsx — A barra de páginas e o "quantos por vez".
 *
 * Usa a `.pagination` do **Bootstrap**, que o tema AdminUX já estiliza em
 * `_table.scss` (cor, hover, estado desabilitado). Nada de CSS novo: se o tema
 * tem, o tema manda.
 *
 * ## A escolha do "quantos por vez" fica GUARDADA
 *
 * Por tela e por navegador, em `localStorage` — o mesmo lugar onde o app já
 * guarda o tema recolhido e o convite de instalar dispensado. Quem gosta de ver
 * 200 por vez não reescolhe a cada visita.
 *
 * **Por que no navegador e não no banco** (decisão de 24/08, com ele): a API
 * recebe o número pela query string e não sabe de onde ele veio. Guardar no
 * navegador custa zero e resolve hoje; se um dia a escolha tiver de seguir a
 * pessoa do desktop para o celular, ela migra para uma preferência de usuário —
 * e esta tela **não muda de forma**, só troca de onde lê o número.
 *
 * ## O que ele NÃO faz
 *
 * Não busca dado. Recebe `total`, `pagina`, `porPagina` e avisa quando mudam —
 * quem carrega é a tela, que é quem conhece os filtros.
 */
import { useEffect, useState } from 'react';

/** As opções de "quantos por vez". 200 é o teto do que cabe numa tela sem doer. */
export const POR_PAGINA = [25, 50, 100, 200] as const;

const PREFIXO = 'caa:porPagina:';

/**
 * Lê a preferência guardada desta tela, ou o padrão.
 *
 * `try/catch` porque `localStorage` **lança** em janela anônima de alguns
 * navegadores e quando o usuário bloqueia dados de site — e uma listagem não
 * pode deixar de abrir por causa de uma preferência.
 */
export function porPaginaGuardado(tela: string, padrao = 50): number {
  try {
    const v = Number(localStorage.getItem(PREFIXO + tela));
    return POR_PAGINA.includes(v as (typeof POR_PAGINA)[number]) ? v : padrao;
  } catch {
    return padrao;
  }
}

function guardar(tela: string, valor: number): void {
  try {
    localStorage.setItem(PREFIXO + tela, String(valor));
  } catch {
    /* sem localStorage: a escolha vale só para esta visita */
  }
}

interface Props {
  /** Nome curto da tela — é a chave da preferência. Ex.: `'ordens-servico'`. */
  tela: string;
  total: number;
  pagina: number;
  porPagina: number;
  onPagina: (p: number) => void;
  onPorPagina: (n: number) => void;
  /** Palavra do que se conta, para a frase ficar em português. */
  rotulo?: string;
}

export function CustomPaginacao({
  tela,
  total,
  pagina,
  porPagina,
  onPagina,
  onPorPagina,
  rotulo = 'registro',
}: Props) {
  const paginas = Math.max(1, Math.ceil(total / porPagina));
  const primeiro = total === 0 ? 0 : (pagina - 1) * porPagina + 1;
  const ultimo = Math.min(pagina * porPagina, total);

  // Página que ficou além do fim (filtrou e sobrou menos) volta para a última
  // que existe. Sem isto a tela mostra "página 7 de 2" e uma lista vazia.
  useEffect(() => {
    if (pagina > paginas) onPagina(paginas);
  }, [pagina, paginas, onPagina]);

  function trocarTamanho(n: number) {
    guardar(tela, n);
    onPorPagina(n);
    onPagina(1); // trocar o tamanho recomeça do topo — a página 7 antiga não quer dizer nada agora
  }

  // A janela de números: no máximo 5, centrada na atual. Listar 40 páginas não
  // ajuda ninguém e estoura no celular.
  const inicio = Math.max(1, Math.min(pagina - 2, paginas - 4));
  const numeros = Array.from({ length: Math.min(5, paginas) }, (_, i) => inicio + i);

  return (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
      <div className="d-flex align-items-center gap-2 small text-secondary">
        <span>
          {total === 0
            ? `Nenhum ${rotulo}`
            : `${primeiro}–${ultimo} de ${total} ${rotulo}${total > 1 ? 's' : ''}`}
        </span>
        <select
          className="form-select form-select-sm w-auto"
          value={porPagina}
          onChange={(e) => trocarTamanho(Number(e.target.value))}
          aria-label="Quantos por vez"
        >
          {POR_PAGINA.map((n) => (
            <option key={n} value={n}>
              {n} por vez
            </option>
          ))}
        </select>
      </div>

      {paginas > 1 && (
        <nav aria-label="Páginas">
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${pagina <= 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => onPagina(pagina - 1)}>
                <i className="bi bi-chevron-left"></i>
              </button>
            </li>
            {numeros.map((n) => (
              <li key={n} className={`page-item ${n === pagina ? 'active' : ''}`}>
                <button className="page-link" onClick={() => onPagina(n)}>
                  {n}
                </button>
              </li>
            ))}
            <li className={`page-item ${pagina >= paginas ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => onPagina(pagina + 1)}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
}

/** Estado de paginação de uma tela, com a preferência já lida do navegador. */
export function usePaginacao(tela: string, padrao = 50) {
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(() => porPaginaGuardado(tela, padrao));
  const [total, setTotal] = useState(0);
  return { pagina, setPagina, porPagina, setPorPagina, total, setTotal };
}
